import React, { useState, useEffect, useRef } from 'react';
import { AgGridReact } from 'ag-grid-react';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-alpine.css';
import { getProducts, addProduct, updateProduct, deleteProduct, getBrands } from '../../utils/api';
import { ProductModal } from './ProductModal';

export const ProductsPage = () => {
    const [rowData, setRowData] = useState([]);
    const [brands, setBrands] = useState([]);
    const [showModal, setShowModal] = useState(false);
    const [isEdit, setIsEdit] = useState(false);
    const [currentProduct, setCurrentProduct] = useState(null);
    const gridRef = useRef();

    useEffect(() => {
        fetchProducts();
        fetchBrands();
    }, []);

    const fetchProducts = async () => {
        try {
            const res = await getProducts({});
            const products = res.data.map(p => ({
                ...p,
                specifications: JSON.parse(p.specifications || '{}'),
                images: JSON.parse(p.images || '[]')
            }));
            setRowData(products);
        } catch (err) {
            console.error(err);
        }
    };

    const fetchBrands = async () => {
        try {
            const res = await getBrands();
            setBrands(res.data);
        } catch (err) {
            console.error(err);
        }
    };

    const columnDefs = [
        { field: 'id', headerName: 'ID', filter: true, sortable: true, minWidth: 70 },
        { field: 'title', headerName: 'Title', filter: true, sortable: true, flex: 2, minWidth: 140 },
        { field: 'description', headerName: 'Description', filter: true, sortable: true, flex: 2, minWidth: 150, cellRenderer: (params) => params.value?.substring(0, 50) + '...' },
        { field: 'category', headerName: 'Category', filter: 'agTextColumnFilter', sortable: true, minWidth: 110 },
        { field: 'brand_name', headerName: 'Brand', filter: true, sortable: true, minWidth: 100 },
        { field: 'price', headerName: 'Price', filter: 'agNumberColumnFilter', sortable: true, valueFormatter: (params) => `$${params.value}`, minWidth: 90 },
        { field: 'update_date', headerName: 'Updated', filter: 'agDateColumnFilter', sortable: true, minWidth: 110 },
        {
            headerName: 'Actions',
            cellRenderer: (params) => (
                <div className="flex flex-wrap gap-1 sm:gap-2">
                    <button onClick={() => handleEdit(params.data)} className="px-2 py-1 bg-blue-500 text-white rounded text-xs sm:text-sm">Edit</button>
                    <button onClick={() => handleDelete(params.data.id)} className="px-2 py-1 bg-red-500 text-white rounded text-xs sm:text-sm">Delete</button>
                </div>
            ),
            width: 140,
            minWidth: 120
        }
    ];

    const defaultColDef = {
        resizable: true,
        sortable: true,
        filter: true
    };

    const onGridReady = (params) => {
        gridRef.current.api = params.api;
    };

    const handleAdd = () => {
        setIsEdit(false);
        setCurrentProduct(null);
        setShowModal(true);
    };

    const handleEdit = (product) => {
        setIsEdit(true);
        setCurrentProduct(product);
        setShowModal(true);
    };

    const handleDelete = async (id) => {
        if (window.confirm('Are you sure?')) {
            try {
                await deleteProduct(id);
                fetchProducts();
            } catch (err) {
                console.error(err);
            }
        }
    };

    const handleModalClose = () => {
        setShowModal(false);
        setCurrentProduct(null);
    };

    const handleModalSubmit = async (formData) => {
        try {
            if (isEdit && currentProduct) {
                await updateProduct(formData);
            } else {
                await addProduct(formData);
            }
            fetchProducts();
            handleModalClose();
        } catch (err) {
            console.error(err);
        }
    };

    return (
        <div className="bg-white p-3 sm:p-4 md:p-6 rounded-lg shadow">
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 mb-4 sm:mb-6">
                <h1 className="text-xl sm:text-2xl font-bold">Products</h1>
                <button onClick={handleAdd} className="px-4 py-2 bg-green-500 hover:bg-green-600 text-white rounded text-sm sm:text-base w-full sm:w-auto">Add Product</button>
            </div>
            <div className="ag-theme-alpine w-full overflow-x-auto" style={{ height: 'min(60vh, 500px)', width: '100%' }}>
                <AgGridReact
                    ref={gridRef}
                    rowData={rowData}
                    columnDefs={columnDefs}
                    defaultColDef={defaultColDef}
                    onGridReady={onGridReady}
                    animateRows={true}
                />
            </div>
            <ProductModal
                show={showModal}
                onClose={handleModalClose}
                onSubmit={handleModalSubmit}
                isEdit={isEdit}
                currentItem={currentProduct}
                brands={brands}
            />
        </div>
    );
};